import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createTextVNode as _createTextVNode, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "income-verification-tile-wrapper" }
const _hoisted_2 = { class: "row income-verification-tile-header" }
const _hoisted_3 = { class: "col col-left" }
const _hoisted_4 = { class: "title" }
const _hoisted_5 = {
  key: 0,
  class: "caption"
}
const _hoisted_6 = { class: "col col-right" }
const _hoisted_7 = { class: "action" }
const _hoisted_8 = { class: "income-verification-tile-content" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "employment-survey-questions-wrapper" }
const _hoisted_11 = {
  key: 1,
  class: "selected-income-sources"
}
const _hoisted_12 = { class: "selected-options" }

import { ref, reactive, onMounted, computed, watch } from "vue";
import type { PropType } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import type { Application, Applicant, SurveyResponse } from "@/models/application";
import type { ApplicantIncomeSourcesRequest } from "@/models/api";
import type { FieldSchema, Option } from "@/models/form";
import type { SurveyResponseViewModel } from "@/models/viewModels";
import { IncomeSourceTypeIds, ProductIds, SurveyQuestionTypeIds } from "@/config/constants";
import { $api } from "@/services/api";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import NextButton from "@/components/shared/NextButton.vue";
import useFormatter from "@/use/formatter";
import useChecklistHelpers from "@/use/checklistHelpers";
import useValidation from "@/use/validation";
import Tile from "@/components/shared/Tile.vue";
import UpdateIncomeSourcesError from "@/components/errors/UpdateIncomeSourcesError.vue";
import useApplicationData from "@/composables/useApplicationData";


export default /*@__PURE__*/_defineComponent({
  __name: 'Income',
  props: {
  applicant: Object as PropType<Applicant>,
  application: Object as PropType<Application>,
},
  emits: ["incomeSourcesUpdated"],
  setup(__props, { emit: __emit }) {

const store = useStore();
const route = useRoute();

const props = __props;

const emit = __emit;

const { replaceObjectWithId } = useChecklistHelpers();
const { formatName } = useFormatter();
const { validate } = useValidation();

const updatingIncomeSources = ref<boolean>(false);
const showIncomeUploadSection = ref<boolean | undefined>(false);
const editSelection = ref<boolean>(false);
const showEmploymentSurveyQuestions = ref<boolean>(false);
const gettingData = ref<boolean>(true);
const employmentStatusResponse = ref<undefined | string>(undefined);
const employerNameResponse = ref<undefined | string>(undefined);
const updateIncomeSourcesError = ref<boolean>(false);
const paycheckFromEmployerChecked = ref<boolean>(false);

// Computed
const { emailAddress, productTypeId } = useApplicationData();

const employmentSurveyQuestionsEnabled = computed<boolean>(() => {
  const productIds = [ProductIds.REFI, ProductIds.PEL, ProductIds.LOC];
  return productIds.includes(productTypeId.value);
});

const path = computed(() => {
  return store.state.appData?.aplication?.metadata?.withCoApplicants === true ||
    store.state.appData?.applicants?.length > 1
    ? "Co-Applicant"
    : "Primary Applicant";
});

const assignedApplicant = computed(() => {
  return store.state.appData.applicants.find((a: Applicant) => a.applicantTypeName === path.value);
});

const surveyResponses = computed(() => {
  return assignedApplicant.value?.surveyResponses || [];
});

const employerNameSurveyResponse = computed<SurveyResponse | undefined>(() =>
  surveyResponses.value?.find(
    (s: SurveyResponse) => s.questionId === SurveyQuestionTypeIds.EmployerName,
  ),
);

const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const referenceId = computed(
  () => route?.params?.referenceId || store.state.appData?.aplication?.referenceId,
);
const fullName = computed(() => getApplicantName());
const selectedIncomeSources = computed(() =>
  store.state.incomeSources?.filter((i: any) => i.isChecked),
);

const validEmploymentSurveyQuestionsForm = computed(() => {
  if (
    (info["Paystub"] === true &&
      employmentStatusResponse.value === undefined &&
      employerNameResponse.value === undefined) ||
    (info["Paystub"] === true && employmentStatusResponse.value === "") ||
    (employmentStatusResponse.value === "Yes" &&
      (employerNameResponse.value === undefined ||
        employerNameResponse.value === null ||
        (employerNameResponse.value && employerNameResponse.value?.trim()?.length <= 0)))
  ) {
    return false;
  } else {
    return true;
  }
});

let checkboxfieldSchema = reactive({} as any);
let incomeSources = reactive([] as any);
let info = reactive({} as any);
let selectedSources = reactive([] as any[]);
let selectedSourceIds = reactive([] as any[]);
let alreadySelectedIds = reactive([] as number[]);

const employmentStatusSchema = reactive<FieldSchema>({
  type: "select",
  label: "Are you currently employed?",
  hideLabel: false,
  required: false,
  validate(schema: FieldSchema, value: string, compare: any) {
    if (value !== undefined) {
      if (value === "No") {
        schema.valid = true;
        const error = `Current employment is required to qualify for this loan program. Please contact our team to discuss at ${emailAddress.value}.`;
        schema.errors = [error];
      } else if (value === "") {
        const defaultErrorMessage = `${schema.label} is required.`;
        schema.valid = false;
        schema.errors = [defaultErrorMessage];
      } else {
        schema.valid = true;
        schema.errors = [];
      }
    } else {
      const defaultErrorMessage = `${schema.label} is required.`;
      schema.valid = false;
      schema.errors = [defaultErrorMessage];
    }
  },
});

const employerNameSchema = reactive<FieldSchema>({
  type: "text",
  label: "What is the name of your employer?",
  hideLabel: false,
  required: false,
});

const employmentStatusOptions = [
  {
    label: "Yes",
    value: "Yes",
  } as Option,
  {
    label: "No",
    value: "No",
  } as Option,
] as Option[];

function getApplicantName() {
  let fullName = "";
  if (assignedApplicant.value?.id && assignedApplicant.value?.id > 0) {

    const firstName = formatName(assignedApplicant.value?.firstName?.toLowerCase());
    let lastName = formatName(assignedApplicant.value?.lastName?.toLowerCase());
   
    if(!lastName.endsWith("'s")){
      lastName += "'s";
    }

    fullName = `${firstName} ${lastName}`;
  }
  
  return fullName;
}

function setEmploymentQuestionSurveyResponses() {
  if (surveyResponses.value?.length > 0) {
    const employmentStatus =
      surveyResponses.value?.find(
        (q: SurveyResponseViewModel) => q.questionId === SurveyQuestionTypeIds.EmploymentStatus,
      )?.response || undefined;

    const employerName =
      surveyResponses?.value?.find(
        (q: SurveyResponseViewModel) => q.questionId === SurveyQuestionTypeIds.EmployerName,
      )?.response || undefined;

    employmentStatusResponse.value = employmentStatus;
    employerNameResponse.value = employerName;

    if (employmentStatusResponse.value === "No") {
      validate(employmentStatusSchema, employmentStatusResponse.value);
      employerNameResponse.value = undefined;
    }
  }
}

function setIncomeSources(updatedSources: any) {
  // reset selectedSources
  selectedSources = [];
  selectedSourceIds = [];
  alreadySelectedIds = [];

  updatedSources.forEach((updatedSource: any) => {
    const newItem =
      incomeSources?.find((incomeSource: any) => incomeSource.id === updatedSource.id) ===
      undefined;
    if (newItem === false) {
      replaceObjectWithId(incomeSources, updatedSource.id, updatedSource);
    } else {
      incomeSources.push(updatedSource);
    }

    info[updatedSource.name] = updatedSource.isChecked;

    if (updatedSource.isChecked) {
      selectedSources.push(updatedSource);
      selectedSourceIds.push(updatedSource.id);
      alreadySelectedIds.push(updatedSource.id);

      // failsafe to remove duplicates
      selectedSources = [...new Set(selectedSources)];
      selectedSourceIds = [...new Set(selectedSourceIds)];
      alreadySelectedIds = [...new Set(alreadySelectedIds)];
    }

    showIncomeUploadSection.value = selectedSourceIds?.length > 0;

    checkboxfieldSchema[updatedSource.name] = {
      type: "checkbox",
      label: updatedSource.title,
      required: false,
      hideLabel: true,
      hintPosition: "bottom",
      ...updatedSource,
    };
  });

  if (
    alreadySelectedIds.includes(IncomeSourceTypeIds.PAYCHECK_FROM_EMPLOYER) &&
    employmentSurveyQuestionsEnabled.value
  ) {
    paycheckFromEmployerChecked.value = true;
    setEmploymentQuestionSurveyResponses();
  }

  editSelection.value = false;
  gettingData.value = false;
}
function handleIncomeSourceSelect(event: any, option: any) {
  if (option.id === IncomeSourceTypeIds.PAYCHECK_FROM_EMPLOYER) {
    paycheckFromEmployerChecked.value = event;
  }
}

async function getIncomeSources() {
  gettingData.value = true;
  const sources = await $api.applicant.getIncomeSources(referenceId.value);

  if (sources && sources.length > 0) {
    await store.dispatch("setIncomeSources", sources);
    setIncomeSources(sources);
  } else {
    gettingData.value = false;

    console.log("Error getIncomeSources():", sources);
  }
}
async function updateIncomeSources() {
  updatingIncomeSources.value = true;
  updateIncomeSourcesError.value = false;

  if (employmentSurveyQuestionsEnabled.value && paycheckFromEmployerChecked.value) {
    validate(employmentStatusSchema, employmentStatusResponse.value);
    validate(employerNameSchema, employerNameResponse.value);
  }

  const valid =
    employmentSurveyQuestionsEnabled.value && paycheckFromEmployerChecked.value
      ? validEmploymentSurveyQuestionsForm.value
      : true;

  if (valid) {
    handleIncomeSourceSelection();

    let applicantIncomeSourcesRequest = {
      referenceId: referenceId.value,
      userId: oidcUser?.value?.sub,
      incomeSourceTypeIds: selectedSourceIds,
    } as ApplicantIncomeSourcesRequest;

    if (employmentSurveyQuestionsEnabled.value && paycheckFromEmployerChecked.value) {
      if (typeof employmentStatusResponse.value == "string") {
        applicantIncomeSourcesRequest.currentlyEmployed = employmentStatusResponse.value;
        applicantIncomeSourcesRequest.employerName = employerNameResponse.value;
      }
    }

    const response = await $api.applicant.updateIncomeSources(applicantIncomeSourcesRequest);

    if (response?.length > 0) {
      store.dispatch("setIncomeSources", response);

      // Get latest survey responses
      if (
        showEmploymentSurveyQuestions.value &&
        applicantIncomeSourcesRequest.incomeSourceTypeIds.includes(
          IncomeSourceTypeIds.PAYCHECK_FROM_EMPLOYER,
        )
      ) {
        await store.dispatch("getApplicantSurveyResponses", assignedApplicant.value?.id);
      }

      // updates UI
      incomeSources = response;
      setIncomeSources(incomeSources);

      // exit out of edit mode
      editSelection.value = false;

      updatingIncomeSources.value = false;

      // get latest stips
      emit("incomeSourcesUpdated");
    } else {
      updatingIncomeSources.value = false;
      updateIncomeSourcesError.value = true;
      console.log("Error updateIncomeSources():", response);
    }
  } else {
    updatingIncomeSources.value = false;
  }
}

function getIncomeSourceId(str: string) {
  return incomeSources?.find((i: any) => i.name === str)?.id;
}

function handleIncomeSourceSelection() {
  let sourceIds = [] as number[];
  let checked = [] as string[];
  for (const [key, value] of Object.entries(info)) {
    // console.log(`key: ${key}, value: ${value}`);
    // if checkbox checked, add to selected denial reasons
    if (value === true) {
      checked.push(key);
      sourceIds.push(getIncomeSourceId(key));
    }
  }
  // failsafe to remove duplicates
  selectedSources = [...new Set(checked)];
  selectedSourceIds = [...new Set(sourceIds)];
}

function resetSelection() {
  editSelection.value = true;
  selectedSources = [];
  selectedSourceIds = [];

  showIncomeUploadSection.value = true;
}

function toggleIncomeSelector() {
  if (
    editSelection.value ||
    (showIncomeUploadSection.value && selectedIncomeSources.value?.length <= 0)
  ) {
    cancel();
  } else {
    showIncomeUploadSection.value = !showIncomeUploadSection.value;
  }
}

function cancel() {
  // discard changes
  setIncomeSources(store.state.incomeSources);

  // exit edit mode
  editSelection.value = false;
}

function toggleIncomeVerificationSection() {
  showIncomeUploadSection.value = true;
}

function resetSelectedIncomeSources() {
  for (let [key, value] of Object.entries(info)) {
    info[key] = false;
  }
}

function resetEmploymentSurveyQuestionsForm() {
  employmentStatusSchema.required = false;
  employerNameSchema.required = false;
  employmentStatusSchema.touched = false;
  employerNameSchema.touched = false;
  employmentStatusSchema.valid = undefined;
  employerNameSchema.valid = undefined;
  employerNameResponse.value = undefined;
  employmentStatusResponse.value = undefined;
  employmentStatusSchema.errors = [];
  employerNameSchema.errors = [];
}

watch(
  () => info["Paystub"],
  value => {
    if (typeof value === "boolean") {
      if (employmentSurveyQuestionsEnabled.value) {
        showEmploymentSurveyQuestions.value = value;
        employmentStatusSchema.required = value;
      }

      if (value === false) {
        resetEmploymentSurveyQuestionsForm();
      }
    }
  },
);

watch(
  () => employmentStatusResponse.value,
  value => {
    if (value === "Yes") {
      employerNameSchema.required = true;
    } else {
      employerNameResponse.value = undefined;
      employerNameSchema.required = false;
    }
  },
  { immediate: true },
);

watch(
  () => employerNameSurveyResponse.value,
  value => {
    employerNameResponse.value = value?.response;
  },
);

onMounted(async () => {
  getIncomeSources();
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Tile, { class: "income-verification-tile" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("span", null, _toDisplayString(fullName.value) + " Income Verification", 1),
              (
                (_unref(selectedSources)?.length === 0 && showIncomeUploadSection.value) ||
                (editSelection.value && _unref(selectedSources)?.length > 0)
              )
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, " To expedite the process, let's get a little information. Please select all income types you've received in the past two years. "))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("a", {
                onClick: toggleIncomeSelector,
                class: "title action"
              }, [
                _createTextVNode(_toDisplayString(!showIncomeUploadSection.value
                  ? "Edit "
                  : editSelection.value ||
                    (selectedIncomeSources.value?.length === 0 && showIncomeUploadSection.value)
                  ? "Cancel"
                  : "Close") + " ", 1),
                (!showIncomeUploadSection.value)
                  ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                      key: 0,
                      class: "icon",
                      icon: ['fas', 'fa-pen']
                    }))
                  : _createCommentVNode("", true)
              ])
            ])
          ])
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_8, [
          _withDirectives(_createVNode(NextButton, {
            class: "w-100",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showIncomeUploadSection.value = !showIncomeUploadSection.value)),
            text: 'Continue'
          }, null, 512), [
            [_vShow, !showIncomeUploadSection.value]
          ]),
          (
            (selectedIncomeSources.value?.length === 0 && showIncomeUploadSection.value) ||
            (editSelection.value && selectedIncomeSources.value?.length > 0 && showIncomeUploadSection.value)
          )
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(incomeSources), (option) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "income-source-wrapper",
                    key: option.name
                  }, [
                    _createVNode(BaseFormField, {
                      modelValue: _unref(info)[option?.name],
                      "onUpdate:modelValue": [($event: any) => ((_unref(info)[option?.name]) = $event), ($event: any) => (handleIncomeSourceSelect($event, option))],
                      schema: _unref(checkboxfieldSchema)[option?.name]
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "schema"]),
                    _withDirectives(_createElementVNode("div", _hoisted_10, [
                      _createVNode(BaseFormField, {
                        modelValue: employmentStatusResponse.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((employmentStatusResponse).value = $event)),
                        schema: employmentStatusSchema,
                        options: employmentStatusOptions
                      }, null, 8, ["modelValue", "schema"]),
                      _withDirectives(_createVNode(BaseFormField, {
                        modelValue: employerNameResponse.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((employerNameResponse).value = $event)),
                        schema: employerNameSchema
                      }, null, 8, ["modelValue", "schema"]), [
                        [_vShow, employmentStatusResponse.value === 'Yes']
                      ])
                    ], 512), [
                      [_vShow, 
                showEmploymentSurveyQuestions.value &&
                _unref(checkboxfieldSchema)[option?.name]?.label === 'Paycheck from an Employer'
              ]
                    ])
                  ]))
                }), 128)),
                _cache[3] || (_cache[3] = _createElementVNode("div", null, " Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation. ", -1)),
                _createVNode(NextButton, {
                  class: "btn-primary",
                  onClick: updateIncomeSources,
                  text: `Continue`,
                  disabled: updatingIncomeSources.value
                }, null, 8, ["disabled"]),
                _withDirectives(_createVNode(UpdateIncomeSourcesError, null, null, 512), [
                  [_vShow, updateIncomeSourcesError.value]
                ])
              ]))
            : _createCommentVNode("", true),
          (selectedIncomeSources.value?.length > 0 && showIncomeUploadSection.value && !editSelection.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _cache[4] || (_cache[4] = _createElementVNode("p", { class: "selected-income-sources-title" }, "Selected income sources", -1)),
                _createElementVNode("ul", _hoisted_12, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedIncomeSources.value, (selectedOption, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      class: "selected-option",
                      key: index
                    }, _toDisplayString(selectedOption.title), 1))
                  }), 128))
                ]),
                _createElementVNode("a", { onClick: resetSelection }, "edit income sources")
              ]))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, !gettingData.value]
        ])
      ]),
      _: 1
    })
  ]))
}
}

})